import React, { useEffect, useRef, useState, useCallback } from 'react';

// ── CarRunnerAuto — 3차선 러너 (Subway Surfers 스타일) ─────────
// 3차선 도로에서 좌/우로 이동하며 장애물 피하고 코인·렌치 수집.
// 1번 충돌 시 게임 오버.

const W = 360;
const H = 520;
const LANE_COUNT = 3;
const LANE_W = W / LANE_COUNT;
const PLAYER_Y = H - 100;
const PLAYER_R = 26;
const ITEM_R = 20;
const BEST_KEY = 'ddabak_egg_best_auto';

const OBSTACLES = ['🚙','🚌','🚓','🚐','🚛','🚧'];   // 충돌 시 GAME OVER
const COINS     = ['🪙','💰'];                        // +1
const TOOLS     = ['🔧','⚙️','🛞','🔋'];             // +5
const PLAYER    = '🚗';

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function drawEmoji(ctx, emoji, x, y, size = 40) {
  ctx.save();
  ctx.font = `${size}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, x, y);
  ctx.restore();
}

export default function CarRunnerAuto({ onClose }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(null);
  const rafRef    = useRef(null);
  const [phase, setPhase] = useState('idle');
  const [score, setScore] = useState(0);
  const [best,  setBest]  = useState(
    () => parseInt(localStorage.getItem(BEST_KEY) || '0')
  );

  const initState = useCallback(() => ({
    items: [],
    particles: [],
    lane: 1,                 // 0=좌 1=중 2=우
    laneAnimX: W / 2,        // 부드러운 이동용
    frame: 0,
    score: 0,
    speed: 4,                // 객체 낙하 속도 (난이도 ↑)
    spawnTimer: 0,
    roadOffset: 0,           // 도로 줄 애니메이션
  }), []);

  function laneCenterX(lane) {
    return LANE_W * lane + LANE_W / 2;
  }

  function spawnItem(s) {
    const r = Math.random();
    let type, emoji;
    if (r < 0.6)      { type = 'obstacle'; emoji = pick(OBSTACLES); }
    else if (r < 0.85){ type = 'coin';     emoji = pick(COINS); }
    else              { type = 'tool';     emoji = pick(TOOLS); }

    s.items.push({
      id: Math.random(),
      lane: Math.floor(Math.random() * LANE_COUNT),
      y: -40,
      type, emoji,
    });
  }

  function spawnParticles(arr, x, y, color, text) {
    for (let i = 0; i < 6; i++) {
      arr.push({
        x, y,
        vx: (Math.random() - 0.5) * 4,
        vy: -1 - Math.random() * 3,
        life: 1,
        color, text,
      });
    }
  }

  const start = useCallback(() => {
    if (phase === 'idle' || phase === 'dead') {
      stateRef.current = initState();
      setScore(0);
      setPhase('playing');
    }
  }, [phase, initState]);

  const moveLane = useCallback((dir) => {
    if (!stateRef.current || phase !== 'playing') return;
    const s = stateRef.current;
    s.lane = Math.max(0, Math.min(LANE_COUNT - 1, s.lane + dir));
  }, [phase]);

  // 키보드
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === 'Space' && (phase === 'idle' || phase === 'dead')) {
        e.preventDefault(); start(); return;
      }
      if (e.code === 'ArrowLeft')  { e.preventDefault(); moveLane(-1); }
      if (e.code === 'ArrowRight') { e.preventDefault(); moveLane(+1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, start, moveLane]);

  // 터치 / 클릭 — 화면 좌/우 영역 구분
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let touchStartX = null;

    const onDown = (e) => {
      e.preventDefault();
      if (phase === 'idle' || phase === 'dead') { start(); return; }
      const evt = e.touches ? e.touches[0] : e;
      const rect = canvas.getBoundingClientRect();
      const x = (evt.clientX - rect.left) * (W / rect.width);
      touchStartX = x;
      // 즉시 클릭으로도 차선 변경 (탭 영역 기반)
      if (x < W / 3)        stateRef.current && (stateRef.current.lane = 0);
      else if (x > 2*W / 3) stateRef.current && (stateRef.current.lane = LANE_COUNT - 1);
      else                  stateRef.current && (stateRef.current.lane = 1);
    };
    const onUp = (e) => {
      if (touchStartX === null) return;
      const evt = e.changedTouches ? e.changedTouches[0] : e;
      if (!evt) { touchStartX = null; return; }
      const rect = canvas.getBoundingClientRect();
      const endX = (evt.clientX - rect.left) * (W / rect.width);
      const dx = endX - touchStartX;
      if (Math.abs(dx) > 30) {
        if (dx > 0) moveLane(+1);
        else        moveLane(-1);
      }
      touchStartX = null;
    };

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mouseup',   onUp);
    canvas.addEventListener('touchstart', onDown, { passive: false });
    canvas.addEventListener('touchend',   onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mouseup',   onUp);
      canvas.removeEventListener('touchstart', onDown);
      canvas.removeEventListener('touchend',   onUp);
    };
  }, [phase, start, moveLane]);

  // 게임 루프
  useEffect(() => {
    if (!stateRef.current) stateRef.current = initState();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let currentPhase = phase;

    function loop() {
      const s = stateRef.current;
      ctx.clearRect(0, 0, W, H);

      // 배경 (회색 메탈)
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#475569');
      bg.addColorStop(1, '#1e293b');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // 도로 (가운데 영역)
      ctx.fillStyle = '#334155';
      ctx.fillRect(0, 0, W, H);

      // 차선 구분선 (점선, 애니메이션)
      ctx.strokeStyle = '#facc15';
      ctx.lineWidth = 3;
      ctx.setLineDash([18, 14]);
      ctx.lineDashOffset = -s.roadOffset;
      for (let i = 1; i < LANE_COUNT; i++) {
        ctx.beginPath();
        ctx.moveTo(LANE_W * i, 0);
        ctx.lineTo(LANE_W * i, H);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      // 도로 가장자리
      ctx.fillStyle = '#fde047';
      ctx.fillRect(0, 0, 4, H);
      ctx.fillRect(W - 4, 0, 4, H);

      if (currentPhase === 'playing') {
        s.frame++;
        s.speed = Math.min(8, 4 + s.frame / 600);
        s.roadOffset += s.speed;

        // 부드러운 차선 이동
        const targetX = laneCenterX(s.lane);
        s.laneAnimX += (targetX - s.laneAnimX) * 0.25;

        // 객체 생성
        s.spawnTimer++;
        const interval = Math.max(22, 55 - Math.floor(s.frame / 240) * 3);
        if (s.spawnTimer >= interval) {
          s.spawnTimer = 0;
          spawnItem(s);
        }

        // 객체 이동 + 충돌
        for (let i = s.items.length - 1; i >= 0; i--) {
          const it = s.items[i];
          it.y += s.speed;
          const ix = laneCenterX(it.lane);

          // 충돌 (같은 차선 + Y 근접)
          const sameLane = Math.abs(ix - s.laneAnimX) < LANE_W * 0.6;
          const closeY = Math.abs(it.y - PLAYER_Y) < PLAYER_R + ITEM_R - 6;
          if (sameLane && closeY) {
            if (it.type === 'obstacle') {
              spawnParticles(s.particles, ix, it.y, '#52525B', '💥');
              currentPhase = 'dead';
              setPhase('dead');
            } else if (it.type === 'coin') {
              s.score += 1;
              setScore(s.score);
              spawnParticles(s.particles, ix, it.y, '#facc15', '+1');
            } else { // tool
              s.score += 5;
              setScore(s.score);
              spawnParticles(s.particles, ix, it.y, '#FF6F0F', '+5');
            }
            s.items.splice(i, 1);
            continue;
          }

          if (it.y > H + 30) s.items.splice(i, 1);
        }

        // 시간 점수 (지나간 거리)
        if (s.frame % 30 === 0) {
          s.score += 1;
          setScore(s.score);
        }

        // 파티클
        for (let i = s.particles.length - 1; i >= 0; i--) {
          const p = s.particles[i];
          p.x += p.vx; p.y += p.vy; p.vy += 0.2; p.life -= 0.025;
          if (p.life <= 0) s.particles.splice(i, 1);
        }

        if (currentPhase === 'dead') {
          setBest(prev => {
            const next = Math.max(prev, s.score);
            localStorage.setItem(BEST_KEY, String(next));
            return next;
          });
        }
      }

      // 객체 그리기
      s.items.forEach(it => {
        const ix = laneCenterX(it.lane);
        drawEmoji(ctx, it.emoji, ix, it.y, 40);
      });

      // 플레이어 차
      ctx.save();
      // 그림자
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      ctx.ellipse(s.laneAnimX, PLAYER_Y + 24, 26, 7, 0, 0, Math.PI * 2);
      ctx.fill();
      drawEmoji(ctx, PLAYER, s.laneAnimX, PLAYER_Y, 50);
      ctx.restore();

      // 파티클
      s.particles.forEach(p => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(p.text, p.x, p.y);
        ctx.globalAlpha = 1;
      });

      // UI: 점수 (우상단)
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.font = 'bold 30px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(String(s.score), W - 18, 42);
      ctx.fillStyle = '#facc15';
      ctx.fillText(String(s.score), W - 20, 40);

      // UI: 속도 표시 (좌상단)
      ctx.fillStyle = '#cbd5e1';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`속도 ${s.speed.toFixed(1)}`, 16, 32);
      ctx.fillStyle = '#A1A1AA';
      ctx.font = '14px sans-serif';
      ctx.fillText('🔧 정비 모드', 16, 52);

      // 시작 화면
      if (currentPhase === 'idle') {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 32px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('🔧 셈셈셈!', W / 2, H / 2 - 60);
        ctx.font = 'bold 22px sans-serif';
        ctx.fillStyle = '#facc15';
        ctx.fillText('정비 완료!', W / 2, H / 2 - 28);
        ctx.font = '16px sans-serif';
        ctx.fillStyle = '#fff';
        ctx.fillText('차를 좌우로 이동해 장애물 피하기', W / 2, H / 2 + 14);
        ctx.font = '13px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText('🪙 코인 +1   🔧 부품 +5   🚙 충돌 = GAME OVER', W / 2, H / 2 + 42);
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = '12px sans-serif';
        ctx.fillText('← → 키 / 화면 좌우 탭 / 스와이프', W / 2, H / 2 + 70);
      }

      // 게임 오버 카드
      if (currentPhase === 'dead') {
        ctx.fillStyle = 'rgba(0,0,0,0.65)';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 130, H / 2 - 110, 260, 220, 20);
        ctx.fill();

        ctx.fillStyle = '#1e293b';
        ctx.font = 'bold 26px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('견인 출동 🚛', W / 2, H / 2 - 65);

        ctx.fillStyle = '#374151';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText('점수', W / 2 - 55, H / 2 - 28);
        ctx.fillText('최고', W / 2 + 55, H / 2 - 28);

        ctx.font = 'bold 36px sans-serif';
        ctx.fillStyle = '#1e293b';
        ctx.fillText(String(s.score), W / 2 - 55, H / 2 + 14);
        ctx.fillStyle = '#facc15';
        ctx.fillText(String(Math.max(s.score, parseInt(localStorage.getItem(BEST_KEY) || '0'))), W / 2 + 55, H / 2 + 14);

        ctx.fillStyle = '#1e293b';
        ctx.beginPath();
        ctx.roundRect(W / 2 - 90, H / 2 + 44, 180, 42, 12);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px sans-serif';
        ctx.fillText('다시 도전 🔄', W / 2, H / 2 + 70);
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, initState]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '12px',
      }}
    >
      <button
        onClick={e => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute', top: '16px', right: '20px',
          background: 'rgba(255,255,255,0.2)', border: 'none',
          color: '#fff', fontSize: '22px', cursor: 'pointer',
          borderRadius: '8px', padding: '4px 10px', fontWeight: '700',
          backdropFilter: 'blur(4px)',
        }}>
        ✕
      </button>

      {best > 0 && (
        <div style={{ color: '#facc15', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
          🏆 최고 기록: {best}
        </div>
      )}

      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'block',
          maxWidth: '95vw',
          maxHeight: '80vh',
          objectFit: 'contain',
          touchAction: 'none',
          cursor: 'pointer',
        }}
      />

      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '4px' }}>
        ← → 키 · 화면 좌·중·우 탭 · 스와이프
      </p>
    </div>
  );
}
